import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMsal } from '@azure/msal-react';
import { ApiService } from '../services/apiService';
import {
  HttpPartnerAliasService,
  type PartnerAliasService,
} from '../services/partnerAliasService';
import type { PartnerAlias } from '../models/partnerAlias';
import { formatPartnerDisplayName } from '../utils/formatters';
import { logApiError } from '../services/apiErrorMessages';

const normalize = (name: string): string => name.trim();

interface PartnerAliasContextValue {
  loading: boolean;
  getAlias: (originalName: string | null | undefined) => string | undefined;
  getDisplayName: (originalName: string | null | undefined) => string | null | undefined;
  upsertAlias: (originalName: string, alias: string) => Promise<void>;
  deleteAlias: (originalName: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const PartnerAliasContext = createContext<PartnerAliasContextValue | undefined>(undefined);

interface ProviderProps {
  children: React.ReactNode;
  service?: PartnerAliasService;
}

export const PartnerAliasProvider: React.FC<ProviderProps> = ({ children, service: serviceOverride }) => {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;

  const httpService = useMemo<PartnerAliasService | null>(() => {
    if (serviceOverride || !account) return null;
    return new HttpPartnerAliasService(new ApiService(instance, account));
  }, [serviceOverride, instance, account]);

  const service = serviceOverride ?? httpService;

  const [aliasMap, setAliasMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState<boolean>(true);

  const applyList = useCallback((list: PartnerAlias[]) => {
    const next = new Map<string, string>();
    for (const a of list) {
      const key = a.normalizedName?.trim() || normalize(a.originalName ?? '');
      if (key) next.set(key, a.alias);
    }
    setAliasMap(next);
  }, []);

  const refresh = useCallback(async () => {
    if (!service) {
      setAliasMap(new Map());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await service.list();
      applyList(list);
    } catch (err) {
      logApiError('PartnerAliasContext.refresh', err);
    } finally {
      setLoading(false);
    }
  }, [service, applyList]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const getAlias = useCallback(
    (originalName: string | null | undefined): string | undefined => {
      if (originalName == null) return undefined;
      return aliasMap.get(normalize(originalName));
    },
    [aliasMap],
  );

  const getDisplayName = useCallback(
    (originalName: string | null | undefined): string | null | undefined =>
      formatPartnerDisplayName(originalName, originalName == null ? undefined : aliasMap.get(normalize(originalName))),
    [aliasMap],
  );

  const upsertAlias = useCallback(
    async (originalName: string, alias: string) => {
      if (!service) throw new Error('No authenticated account; cannot manage aliases.');
      const saved = await service.upsert(originalName, alias);
      setAliasMap((prev) => {
        const next = new Map(prev);
        next.set(saved.normalizedName?.trim() || normalize(originalName), saved.alias);
        return next;
      });
    },
    [service],
  );

  const deleteAlias = useCallback(
    async (originalName: string) => {
      if (!service) throw new Error('No authenticated account; cannot manage aliases.');
      await service.remove(originalName);
      setAliasMap((prev) => {
        const next = new Map(prev);
        next.delete(normalize(originalName));
        return next;
      });
    },
    [service],
  );

  const value = useMemo<PartnerAliasContextValue>(
    () => ({ loading, getAlias, getDisplayName, upsertAlias, deleteAlias, refresh }),
    [loading, getAlias, getDisplayName, upsertAlias, deleteAlias, refresh],
  );

  return <PartnerAliasContext.Provider value={value}>{children}</PartnerAliasContext.Provider>;
};

export const usePartnerAliases = (): PartnerAliasContextValue => {
  const ctx = useContext(PartnerAliasContext);
  if (!ctx) {
    throw new Error('usePartnerAliases must be used within a PartnerAliasProvider');
  }
  return ctx;
};
